import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const BatchFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  onCreateBatch,
  totalBatches,
  activeBatches 
}) => {
  const statusOptions = [
    { value: 'all', label: 'All Status', count: totalBatches },
    { value: 'processing', label: 'Processing', count: activeBatches?.processing || 0 },
    { value: 'completed', label: 'Completed', count: activeBatches?.completed || 0 },
    { value: 'failed', label: 'Failed', count: activeBatches?.failed || 0 },
    { value: 'paused', label: 'Paused', count: activeBatches?.paused || 0 }
  ];

  const sizeRanges = [
    { value: 'all', label: 'All Sizes' },
    { value: 'small', label: '1-10 invoices' },
    { value: 'medium', label: '11-50 invoices' },
    { value: 'large', label: '51+ invoices' }
  ];

  const handleStatusFilter = (status) => {
    onFilterChange({ ...filters, status });
  };

  const handleSizeFilter = (size) => {
    onFilterChange({ ...filters, size });
  };

  const handleDateRangeChange = (field, value) => {
    onFilterChange({ 
      ...filters, 
      dateRange: { ...filters?.dateRange, [field]: value } 
    });
  };

  const handleSearchChange = (e) => {
    onFilterChange({ ...filters, search: e?.target?.value });
  };

  const hasActiveFilters = filters?.status !== 'all' || 
                          filters?.size !== 'all' || 
                          filters?.search || 
                          filters?.dateRange?.start || 
                          filters?.dateRange?.end;

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
            <Icon name="Filter" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Filter Batches</h2>
            <p className="text-sm text-muted-foreground">
              {totalBatches} total batches • {activeBatches?.processing || 0} processing
            </p>
          </div>
        </div>
        
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={onCreateBatch}
        >
          Create New Batch
        </Button>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        {/* Search */}
        <div className="lg:col-span-1">
          <Input
            type="search"
            placeholder="Search batch ID..."
            value={filters?.search}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>

        {/* Date Range */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-2">
          <Input
            type="date"
            label="From Date"
            value={filters?.dateRange?.start}
            onChange={(e) => handleDateRangeChange('start', e?.target?.value)}
          />
          <Input
            type="date"
            label="To Date"
            value={filters?.dateRange?.end}
            onChange={(e) => handleDateRangeChange('end', e?.target?.value)}
          />
        </div>

        {/* Clear Filters */}
        <div className="lg:col-span-1 flex items-end">
          {hasActiveFilters && (
            <Button
              variant="outline"
              iconName="X"
              iconPosition="left"
              onClick={onClearFilters}
              className="w-full"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
      {/* Status Filter Buttons */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-foreground mb-2">
          Filter by Status
        </label>
        <div className="flex flex-wrap gap-2">
          {statusOptions?.map((option) => (
            <button
              key={option?.value}
              onClick={() => handleStatusFilter(option?.value)}
              className={`
                inline-flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium
                border transition-all duration-150 hover:scale-102
                ${filters?.status === option?.value
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }
              `}
            >
              <span>{option?.label}</span>
              <span className={`
                inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5
                text-xs font-semibold rounded-full
                ${filters?.status === option?.value
                  ? 'bg-primary-foreground/20 text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
                }
              `}>
                {option?.count}
              </span>
            </button>
          ))}
        </div>
      </div>
      {/* Size Filter Buttons */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Filter by Batch Size
        </label>
        <div className="flex flex-wrap gap-2">
          {sizeRanges?.map((range) => (
            <button
              key={range?.value}
              onClick={() => handleSizeFilter(range?.value)}
              className={`
                px-3 py-2 rounded-md text-sm font-medium border transition-all duration-150
                ${filters?.size === range?.value
                  ? 'bg-secondary text-secondary-foreground border-secondary'
                  : 'bg-muted text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                }
              `}
            >
              {range?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BatchFilters;

